import React, { useState, useRef, useEffect } from 'react';
import styles from './Header.module.scss';
import { useCookies } from 'react-cookie';
import combatTvLogo from '../../assets/images/CTV-Logo-color.svg';
import { ButtonStyleType, TextSize } from "../../types/button.types";
import avatarIcon from '../../assets/images/avatar.png';
import { APP_ROUTES, Routes } from "../../types/constants";
import Button from "../Buttons/Button/Button";
import { useAppState } from "../../providers/app-state.provider";
import SidePanel from "../SidePanel/SidePanel";
import { useUserState } from "../../providers/user-state.provider.ts";
import router from "../../providers/router";
import { useAuthModal } from "../../providers/auth-modal.provider";
import { useSubscriptionStatus } from "../../providers/subscription-status.provider";
import { isAdmin } from "../../utils/roles";
import GlobalSearchOverlay from "../Search/GlobalSearchOverlay";

interface HeaderProps { }

const Header = (props: HeaderProps) => {
    const { isSidePanelOpen, cycleSidePanel } = useAppState();
    const userData = useUserState();
    const [cookies] = useCookies(['user']);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const userInfo = userData.getUserInfo();
    const { openModal } = useAuthModal();
    const { hasActiveSubscription } = useSubscriptionStatus();
    const isAdminUser = isAdmin(userInfo?.roles);
    const baseRoutes = isAdminUser
        ? [...APP_ROUTES, { name: 'Admin', route: Routes.ADMIN }]
        : APP_ROUTES;
    const routes = hasActiveSubscription
        ? baseRoutes.filter((route) => route.route !== Routes.PRICING)
        : baseRoutes;

    const navigate = (route: Routes | string) => {
        router.navigate(route).then();
    }

    const logoutUser = () => {
        userData.clearUserInfo();
        setIsMenuOpen(false);
        navigate(Routes.HOME);
    }

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setIsMenuOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div>
            <div>
                {isSidePanelOpen && <SidePanel />}
            </div>
            <div className={styles['banner']}>
                <span className={"material-symbols-outlined " + styles['menu-button']} onClick={() => cycleSidePanel()}>menu</span>
                <div className={styles['logo']}>
                    <a href={'/'} className={styles['logo-image']} >
                        <img src={combatTvLogo} alt={'oh no!'} />
                    </a>
                </div>
                <div className={styles['routes-container']}>
                    {
                        routes.length > 0 && routes.map(route => {
                            return (
                                <a key={route.name} href={route.route} onClick={() => navigate(route.route)} className={styles['route']}>{route.name?.toUpperCase()}</a>
                            )
                        })
                    }
                </div>
                <div className={styles['rhs-button-wrapper']} ref={menuRef}>
                    <button
                        type="button"
                        className={styles['search-button']}
                        onClick={() => {
                            setIsMenuOpen(false);
                            setIsSearchOpen(true);
                        }}
                        aria-label="Open search"
                    >
                        <span className="material-symbols-outlined">search</span>
                    </button>
                    {
                        cookies.user ? (
                            <>
                                <img src={avatarIcon} alt="Avatar" className={"rounded cursor-pointer " + styles['profile-avatar']} onClick={toggleMenu} />
                                {isMenuOpen && (
                                    <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg z-50 p-4 flex flex-col max-h-[70vh] overflow-y-auto">
                                        <div className="flex items-center justify-center mb-4 gap-2">
                                            <img src={avatarIcon} alt="" className="w-8 rounded-full cursor-pointer" />
                                            <div>
                                                <p className="font-medium">Welcome</p>
                                                <p className="text-gray-500 text-sm">{userData.getUserInfo()?.username || 'User'}</p>
                                            </div>
                                        </div>
                                        <div className="flex flex-col">
                                            <a href={Routes.PROFILE} onClick={() => navigate(Routes.PROFILE)} className="py-2 px-4 hover:bg-gray-100 rounded-md mb-2 text-gray-700">Account</a>
                                            <a
                                                href={Routes.MY_LIBRARY}
                                                onClick={() => navigate(Routes.MY_LIBRARY)}
                                                className="py-2 px-4 hover:bg-gray-100 rounded-md mb-2 text-gray-700"
                                            >
                                                My Library
                                            </a>
                                            <button type="button" onClick={logoutUser} className="py-2 px-4 hover:bg-gray-100 rounded-md text-gray-700 text-left">Sign Out</button>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className={styles['sign-in-actions']}>
                                <div className={styles['sign-in-desktop']}>
                                    <Button
                                        text={'SIGN IN'}
                                        styleType={ButtonStyleType.FILL}
                                        textSize={TextSize.MEDIUM}
                                        style={styles['sign-in-button']}
                                        callback={() => openModal('sign_in')}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className={styles['sign-in-icon']}
                                    onClick={() => openModal('sign_in')}
                                    aria-label="Sign in"
                                >
                                    <span className="material-symbols-outlined">login</span>
                                </button>
                            </div>
                        )
                    }
                </div>
            </div>
            {isSearchOpen ? (
                <GlobalSearchOverlay
                    onClose={() => setIsSearchOpen(false)}
                    onNavigate={(route) => {
                        setIsSearchOpen(false);
                        navigate(route);
                    }}
                />
            ) : null}
        </div>
    );
}

export default Header;
